import { useSearchParams } from 'react-router-dom'
import { useEffect, useMemo, useState } from 'react'
import { ContactDrawer } from 'app/components/contacts/ContactDrawer'
import { ItemsTitle } from 'app/components/shared/styled/ItemsTitle'
import SimpleTable from 'app/components/table/SimpleTable'
import { FilterValueType, FilterableColumn } from 'app/filter.utils'
import { useListTraversal } from 'app/hooks/useListTraversal'
import type { ContactJson } from 'app/types/entities'
import type { TransactionWithContact } from 'app/types/transaction'
import { notifyError } from 'app/services/notify.client'
import { useContactStore } from 'app/stores/StoreContext'
import { defaultColumns } from 'app/table-columns/revenue-details'
import { gqlFetchList } from 'app/lib/graphqlClient'
import { bg } from 'lib/background'

const filterableColumns = (): FilterableColumn[] => [
  {
    attribute: `locationId`,
    displayName: `Location`,
    optionsSource: `/api/locations`,
    type: FilterValueType.id,
    uniqOperator: `in`,
  } as FilterableColumn,
]

type RevenueDetailsProps = {
  fromDate: any
  toDate: any
  filterTransactionsBy: `production` | `adjustments` | `all`
  locationIds: string[]
  title: string
  subtitle?: string
  onClose: () => void
}

export function RevenueDetails({
  fromDate,
  toDate,
  filterTransactionsBy,
  locationIds,
  title,
  subtitle,
  onClose,
}: RevenueDetailsProps) {
  const [searchParams, setSearchParams] = useSearchParams()
  const [loadingContact, setLoadingContact] = useState(false)
  const contactStore = useContactStore()

  const [page, setPage] = useState(0)
  const [items, setItems] = useState<TransactionWithContact[]>()
  const [haveMore, setHaveMore] = useState(true)
  const [columnFilters, setColumnFilters] = useState<string>(searchParams.get(`filters`) ?? ``)
  const [focusedContact, setFocusedContact] = useState<ContactJson>()
  const [openDrawer, setOpenDrawer] = useState(false)
  const [total, setTotal] = useState(0)
  const [txLoading, setTxLoading] = useState(false)

  const scrollHandler: React.UIEventHandler<HTMLDivElement> = (event: any) => {
    let element = event.target
    if (haveMore && !txLoading && Math.abs(element.scrollHeight - element.clientHeight - element.scrollTop) < 800) {
      setPage(page + 1)
    }
  }

  const onFiltersChange = (filters: string) => {
    setColumnFilters(filters)
    setHaveMore(true)
    setPage(0)
  }

  useEffect(() => {
    if (fromDate && toDate && haveMore && !txLoading) {
      const typeFilter =
        filterTransactionsBy === `production`
          ? `type:Procedure`
          : filterTransactionsBy === `adjustments`
            ? `type:Adjustment`
            : `(type:Procedure OR type:Adjustment)`
      const terms = [
        typeFilter,
        `transactionDate:[${fromDate} TO ${toDate}]`,
        locationIds.length && `locationId:(${locationIds.join(` OR `)})`,
      ]
        .filter(Boolean)
        .join(` AND `)
      setTxLoading(true)
      gqlFetchList<TransactionWithContact>(
        'transactions',
        { search: terms, pagination: { page, limit: 100 }, sort: { field: `transactionDate`, direction: `DESC` } },
        'id amount contactId patientId transactionDate procedureCode locationId',
      )
        .then((data) => {
          if (!data?.hits?.length) {
            setHaveMore(false)
          } else {
            setHaveMore(true)
          }
          setTotal(data?.total || 0)
          setItems((items) => {
            if (page === 0) {
              return data?.hits || []
            } else {
              let newItems = data?.hits || []
              let needToMergePages = true
              while (needToMergePages) {
                if (newItems.at(0) && items?.at(-1) && newItems.at(0)?.contactId === items?.at(-1)?.contactId) {
                  newItems.at(0)!.procedureCode.forEach((code) => {
                    if (!items.at(-1)!.procedureCode.includes(code)) {
                      items.at(-1)!.procedureCode.push(...newItems.at(0)!.procedureCode)
                    }
                  })
                  items.at(-1)!.amount = items.at(-1)!.amount
                    ? items.at(-1)!.amount! + (newItems.at(0)!.amount || 0)
                    : newItems.at(0)!.amount
                  newItems.shift()
                } else {
                  needToMergePages = false
                }
              }
              return (items || []).concat(newItems)
            }
          })
        })
        .finally(() => setTxLoading(false))
    }
  }, [
    page,
    filterTransactionsBy,
    columnFilters,
    fromDate,
    toDate,
    locationIds,
    searchParams.get(`workspaceId`),
    searchParams.get(`join`),
    searchParams.get(`integrationStatus`),
  ])

  useEffect(() => {
    if (searchParams.has(`drawer`) && searchParams.get(`drawer`) !== `` && !openDrawer) {
      // oxlint-disable-next-line react/immutability -- existing closure or mutable API pattern is intentional
      contactHandler(searchParams.get(`drawer`)!)
    }
  }, [searchParams.get(`drawer`)])

  const closeDrawerHandler = () => {
    setFocusedContact(undefined)
    setOpenDrawer(false)
    setSearchParams((prev) => {
      prev.delete(`drawer`)
      return prev
    })
  }

  function loadContact(contact?: ContactJson) {
    let url = new URL(document.location.href)
    setFocusedContact(undefined)
    let contactId = url.searchParams.get(`drawer`) || ``
    if (!contactId) {
      setOpenDrawer(false)
      return
    }
    bg(async () => {
      setLoadingContact(true)
      try {
        const result = await contactStore.fetchOne(contactId)
        const response = (result.data as any)?.contact as ContactJson | undefined
        if (response?.id) {
          setFocusedContact(response)
        } else {
          notifyError(`Contact not found`)
          url.searchParams.delete(`drawer`)
          history.replaceState({}, ``, url.toString())
          setOpenDrawer(false)
          return
        }
      } finally {
        setLoadingContact(false)
      }
    })
  }

  const contactHandler = (contactId: string) => {
    if (focusedContact?.id === contactId) return
    bg(async () => {
      setSearchParams((prev) => {
        prev.set(`drawer`, contactId)
        return prev
      })
      setFocusedContact(undefined)
      setOpenDrawer(true)
      setLoadingContact(true)
      try {
        const result = await contactStore.fetchOne(contactId)
        const data = (result.data as any)?.contact as ContactJson | undefined
        if (!data || result.error) {
          notifyError((result.error as any)?.message ?? `Contact not found`)
          setFocusedContact(undefined)
          setOpenDrawer(false)
        } else {
          setFocusedContact(data)
          setOpenDrawer(true)
        }
      } finally {
        setLoadingContact(false)
      }
    })
  }

  const contactTraversalItems = useMemo(() => {
    const uniqueContacts: { id?: string }[] = []
    const seen = new Set<string>()

    for (const item of items ?? []) {
      if (!item?.contactId) continue
      if (seen.has(item.contactId)) continue

      seen.add(item.contactId)
      uniqueContacts.push({ id: item.contactId })
    }

    return uniqueContacts
  }, [items])

  const contactTraversal = useListTraversal<{ id?: string }>(
    contactTraversalItems,
    focusedContact?.id ? { id: focusedContact.id } : undefined,
    (item) => {
      if (item.id) contactHandler(item.id)
    },
  )

  return (
    <div x-component="RevenueDetails" className="flex h-[100vh] max-h-[100vh] flex-col overflow-hidden px-5">
      <div className="flex h-full w-full">
        <div className="flex h-full w-full flex-col overflow-hidden">
          <ItemsTitle title={title} subtitle={subtitle} items={total} onClose={onClose} />
          <SimpleTable
            data={items}
            columns={defaultColumns(contactHandler)}
            onScroll={scrollHandler}
            hideFooter={true}
            onFiltersChange={onFiltersChange}
            filterableColumns={filterableColumns()}
            showColumnOrdering={true}
          />
        </div>
        <ContactDrawer
          openDrawer={openDrawer}
          contact={focusedContact}
          onClose={closeDrawerHandler}
          onChange={loadContact}
          nextContact={contactTraversal.next}
          previousContact={contactTraversal.prev}
          isNavigating={contactTraversal.isNavigating}
        />
      </div>
    </div>
  )
}
